import { DecimalPipe } from '@angular/common';
import { Pipe, PipeTransform, inject, LOCALE_ID } from '@angular/core';

/**
 * Formats table cell values that are numbers (or numeric strings) using the app locale.
 * - With `maxFractionDigits` (>= 0): uses that many fraction digits at most.
 * - Without it: integers render like raw numbers (plain digits); non-integers use at most one fraction digit (locale-aware).
 * Non-finite values fall back to the original string representation.
 */
@Pipe({
  name: 'tableNumberFormat',
  standalone: true,
})
export class TableNumberFormatPipe implements PipeTransform {
  private readonly decimalPipe = new DecimalPipe(inject(LOCALE_ID));

  transform(value: unknown, maxFractionDigits?: number | null): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) {
      return String(value);
    }

    const explicit =
      maxFractionDigits != null && maxFractionDigits >= 0;
    if (explicit) {
      const digits = Math.floor(maxFractionDigits as number);
      return this.decimalPipe.transform(num, `1.0-${digits}`) ?? '';
    }

    // Whole numbers: keep plain string (same as raw interpolation, no grouping surprises).
    if (Number.isInteger(num)) {
      return String(num);
    }

    return this.decimalPipe.transform(num, '1.0-1') ?? '';
  }
}
